"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Container from "@/components/layouts/container";
import styles from "./Header.module.scss";
import AuthModal from "@/components/Modal/Auth/AuthModal";
import { useStore } from "@/hooks/use-store";
import SearchModal from "@/components/Modal/Search/SearchModal";
import UserProfile from "@/components/UserProfile/UserProfile";
import { useMediaQuery } from "@mui/material";
import { getProfile, getProfileBalance } from "@/domains/profile/actions";
import { useEffect, useRef } from "react";
import { getToken, logout } from "@/domains/auth/actions";

export default function Header({ isLogin }: { isLogin: boolean }) {
  const t = useTranslations("Header");
  const { modal, user } = useStore();
  const isTabletOrMobile = useMediaQuery("only screen and (max-width: 1024px)");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  async function getBalance() {
    const _userBalance = await getProfileBalance();

    if (_userBalance !== undefined) {
      user.setUserBalance(_userBalance.amount);

      if (!user?.isAuth) {
        const _user = await getProfile();

        const token = await getToken();

        user.setToken(token ?? "");

        user.setIsAuth(true);
        user.setUser(_user);
      }
    } else {
      logout();
      user.logout();
    }
  }

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (isLogin) {
      getBalance();

      intervalRef.current = setInterval(() => {
        getBalance();
      }, 15000);
    } else if (!isLogin && intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isLogin]);

  return (
    <header className={styles.header}>
      <Container>
        <nav
          className={`${styles.nav}`}
          // className={`${styles.nav} ${styles.new_year}`}
        >
          <div className={styles.nav__leftside}>
            <Link className={styles.logo} href={"/"}>
              <picture>
                <source media="(min-width: 768px)" srcSet="/images/logo-min.svg" />
                <Image
                  className={styles.logo__img}
                  src="/images/logo-s-min.svg"
                  alt="MaxSpin"
                  width={145}
                  height={50}
                  quality={100}
                />
              </picture>
            </Link>

            {!isTabletOrMobile && (
              <button
                className={`${styles.button} ${styles.search_field}`}
                type="button"
                onClick={() => modal.showModal(<SearchModal />)}
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  width="18"
                  height="100%"
                  className={styles.search_field__icon}
                >
                  <use href="/images/sprite.svg#search"></use>
                </svg>
                <span className={styles.search_field__text}>{t("search")}</span>
              </button>
            )}
          </div>

          <div className={styles.actions}>
            {isLogin ? (
              <UserProfile />
            ) : (
              <>
                <button
                  className={`${styles.button} ${styles.button_signin}`}
                  type="button"
                  onClick={() => modal.showModal(<AuthModal activeTab={"login"} />)}
                >
                  {t("auth")}
                </button>
                <button
                  className={`${styles.button} ${styles.button_signup} ${styles.button_accent}`}
                  type="button"
                  onClick={() => modal.showModal(<AuthModal activeTab={"registration"} />)}
                >
                  {t("registration")}
                </button>
              </>
            )}
          </div>
        </nav>
      </Container>
    </header>
  );
}
